import { addToQueryCache, handleAddToCollectionOp, invalidateCache, updateQueryCacheCollection } from "@sg/query";
import { useMemo } from "react";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { z } from "zod";
import useAuthStore from "~/store/auth";
import { AuditableSchema } from "~/types";
import { agent } from "~/util/agent";
import { useIsSuperAdmin } from "../util/useRoles";
import {
  OnboardingResourceType,
  QUERY_ONBOARDING_REVIEW_SESSIONS_KEY,
  QUERY_ONBOARDING_SESSIONS_KEY,
} from "./onboarding";

const AccountGroupSchema = z.object({
  id: z.string().uuid().min(1),
  name: z.string().trim().min(1),
  counterparty_id: z.string().uuid().min(1),
  lei: z.string().optional(),
  telegram_chat_id: z.string().optional(),
});

const CounterpartyCreateSchema = z.object({
  name: z.string().trim().min(1),
  org_id: z.string().trim().min(1),
});

const CounterpartyUpdateSchema = z.object({
  lei: z.string().optional(),
  telegram_chat_id: z.string().optional(),
});

const CounterpartySchema = CounterpartyCreateSchema.merge(AuditableSchema).extend({
  is_deactivated: z.boolean(),
  lei: z.string().optional(),
  telegram_chat_id: z.string().optional(),
});

type AccountGroupResourceType = z.infer<typeof AccountGroupSchema>;
type CounterpartyResourceType = z.infer<typeof CounterpartySchema>;
type CreateCounterpartyInput = Omit<z.infer<typeof CounterpartyCreateSchema>, "external_id">;
type UpdateCounterpartyInput = z.infer<typeof CounterpartyUpdateSchema>;

const QUERY_MARGIN_GROUPS_KEY = "margin-groups";
const QUERY_COUNTERPARTIES_KEY = "counterparties";

const useMarginGroups = (cpId?: string | null) =>
  useQuery(
    [QUERY_MARGIN_GROUPS_KEY, cpId],
    async () => agent(`counterparties/${cpId}/margin_groups/`).json<AccountGroupResourceType[]>(),
    {
      enabled: !!cpId,
    }
  );

const useMarginGroupOnDemand = () => {
  return useMutation((cpId?: string) =>
    cpId ? agent(`counterparties/${cpId}/margin_groups/`).json<AccountGroupResourceType>() : Promise.resolve(undefined)
  );
};

const useCounterparties = (disabled?: boolean) => {
  const authToken = useAuthStore((state) => state.authToken);
  const isSuperAdmin = useIsSuperAdmin();
  const params = useParams();
  const orgId = isSuperAdmin ? params.orgId || "" : "";

  return useQuery(
    [QUERY_COUNTERPARTIES_KEY, "org", orgId],
    async () => agent(`counterparties/${orgId ? `?org_id=${orgId}` : ""}`).json<CounterpartyResourceType[]>(),
    {
      enabled: !disabled && !!authToken,
    }
  );
};

const useOrgCounterparties = (disabled?: boolean) => {
  const authToken = useAuthStore((state) => state.authToken);
  const params = useParams();
  const orgId = params.orgId;

  return useQuery(
    [QUERY_COUNTERPARTIES_KEY, "org", orgId],
    async () => agent(`counterparties/${orgId ? `?org_id=${orgId}` : ""}`).json<CounterpartyResourceType[]>(),
    {
      enabled: !disabled && !!authToken,
    }
  );
};

const useEligibleCounterparties = () => {
  const counterparties = useCounterparties();
  return useMemo(() => {
    let data: CounterpartyResourceType[] = [];
    if (counterparties.data) data = counterparties.data.filter((cp) => !cp.is_deactivated);
    return {
      data: data,
      isLoading: counterparties.isLoading,
    };
  }, [counterparties]);
};

const useGlobalCounterparties = () => {
  const isSuperAdmin = useIsSuperAdmin();

  return useQuery(
    [QUERY_COUNTERPARTIES_KEY, "org", "all"],
    async () => agent(`counterparties/admin`).json<CounterpartyResourceType[]>(),
    {
      enabled: isSuperAdmin,
    }
  );
};

const useTradingCounterparties = (currentCounterpartyId?: string) => {
  const counterparties = useGlobalCounterparties();
  return useMemo(() => {
    let data: CounterpartyResourceType[] = [];
    if (counterparties.data)
      data = counterparties.data.filter((cp) => !cp.is_deactivated && cp.id !== currentCounterpartyId);
    return {
      data: data,
      isLoading: counterparties.isLoading,
    };
  }, [counterparties, currentCounterpartyId]);
};

const useUpdateCounterparty = () => {
  const params = useParams();
  return useMutation<unknown, Error, UpdateCounterpartyInput & { id: string }>(
    (data) => {
      const { id, ...payload } = data;
      return agent.patch(`counterparties/${id}`, { labels: ["updated", "counterparty"], json: payload });
    },
    {
      onSuccess(data, variables) {
        invalidateCache([QUERY_COUNTERPARTIES_KEY, (data as CounterpartyResourceType).id], variables);
        invalidateCache([QUERY_COUNTERPARTIES_KEY, "org", "all"], variables);
        invalidateCache([QUERY_COUNTERPARTIES_KEY, "org", ""], variables);
        invalidateCache([QUERY_COUNTERPARTIES_KEY, "org", params.orgId], variables);
      },
    }
  );
};

const useCounterparty = (id?: string) => {
  const authToken = useAuthStore((state) => state.authToken);

  return useQuery(
    [QUERY_COUNTERPARTIES_KEY, id],
    async () => agent(`counterparties/${id}`).json<CounterpartyResourceType>(),
    {
      enabled: !!id && !!authToken,
    }
  );
};

const createCpLabels = ["created", "counterparty"];

const useCreateCounterparty = () => {
  return useMutation<CounterpartyResourceType, Error, CreateCounterpartyInput>(
    (data) => {
      async function createCounterparty(data: CreateCounterpartyInput) {
        try {
          const cp = await agent
            .post("counterparties/", {
              labels: createCpLabels,
              json: data,
            })
            .json<CounterpartyResourceType>();
          const os = await agent
            .post(`counterparties/${cp.id}/onboarding/`, {
              errLabels: createCpLabels,
            })
            .json<OnboardingResourceType>();
          addToQueryCache<OnboardingResourceType, OnboardingResourceType>(
            (os) => [QUERY_ONBOARDING_SESSIONS_KEY, cp.id, os.id],
            os,
            os
          );
          return Promise.resolve(cp);
        } catch (e) {
          return Promise.reject(e as Error);
        }
      }

      return createCounterparty(data);
    },
    {
      onSuccess(data, variables) {
        handleAddToCollectionOp(QUERY_COUNTERPARTIES_KEY);
        invalidateCache([QUERY_COUNTERPARTIES_KEY, "org", data.org_id], variables);
        invalidateCache([QUERY_COUNTERPARTIES_KEY, "org", ""], variables);
        invalidateCache([QUERY_ONBOARDING_REVIEW_SESSIONS_KEY], variables);
      },
    }
  );
};

const useDeactivateCounterparty = () => {
  return useMutation<unknown, Error, string>(
    (counterpartyId) => {
      return agent.delete(`counterparties/${counterpartyId}`, { labels: ["deactivated", "entity"] });
    },
    {
      onSuccess(data, variables) {
        updateQueryCacheCollection(QUERY_COUNTERPARTIES_KEY, { is_deactivated: true }, variables);
      },
    }
  );
};
export {
  AccountGroupSchema,
  CounterpartyCreateSchema,
  CounterpartyUpdateSchema,
  QUERY_COUNTERPARTIES_KEY,
  useCounterparties,
  useCounterparty,
  useCreateCounterparty,
  useDeactivateCounterparty,
  useEligibleCounterparties,
  useGlobalCounterparties,
  useMarginGroupOnDemand,
  useMarginGroups,
  useOrgCounterparties,
  useTradingCounterparties,
  useUpdateCounterparty,
};
export type { CounterpartyResourceType, CreateCounterpartyInput, UpdateCounterpartyInput };
